import { createElement, type ReactNode } from "react";

// キャラ未マッチ時のフォールバックカラー
export const DEFAULT_COLOR = "#E5E7EB";

export type CharacterEntry = {
  color: string;
  name: string;
  portraitUrl?: string | null;
};

// {{char:ID}}text{{/char}} / {{char-ID}}text{{/char}} および
// {{keyword}}text{{/keyword}} にマッチする正規表現。
//
// AI 出力 lenient 化 (= 2026-05-14、 user 報告 「色がついていない部分があった」 + smoke verify
// 結果): AI が稀に `{{char-ID}}` 形式 (= colon の代わりに hyphen) を生成。 character ID は
// `char-XXX` 形式が convention (= 例 char-detective)、 colon case と hyphen case で capture
// 戦略が **非対称**:
//
// 1. colon case `{{char:char-detective}}`: `char:` prefix を strip して `char-detective` を capture
//    (= group 1)
// 2. hyphen case `{{char-detective}}`: `char-` を strip **せず** に `char-detective` を全体 capture
//    (= group 2、 ID は元から `char-` prefix 込みなので prefix 保持必要)
//
// 過去 PR #290 で `char[:-]` 単一 separator にした際、 hyphen case で `char-` まで消費されて
// capture group が `detective` のみとなり、 charactersMap.get("detective") が miss して
// DEFAULT_COLOR (grey) になる regression があった。 本 PR で 2 branch 化で完全 fix。
//
// prompt 側は `{{char:ID}}` 正格指定維持 (= 仕様としては colon、 parser のみ lenient)。
const MARKUP_REGEX =
  /\{\{(?:char:([^}]+)|(char-[^}]+)|keyword)\}\}([\s\S]*?)\{\{\/(?:char|keyword)\}\}/g;

/**
 * インラインマークアップ文字列を ReactNode 配列にパースする。
 * dangerouslySetInnerHTML を使わず、React 要素として安全にレンダリングするため。
 *
 * @param text パース対象のテキスト
 * @param charactersMap キャラクター ID → CharacterEntry のマップ
 * @returns ReactNode 配列（文字列 + span 要素の混在）
 */
export function parseInlineMarkup(
  text: string,
  charactersMap: Map<string, CharacterEntry>,
  colorOverride?: string,
): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let tagIndex = 0;

  for (const match of text.matchAll(MARKUP_REGEX)) {
    const matchStart = match.index;
    const matchEnd = matchStart + match[0].length;

    // タグ前のプレーンテキストを追加（未知の{{...}}タグは除去）
    if (matchStart > lastIndex) {
      const plain = text.slice(lastIndex, matchStart).replace(/\{\{.*?\}\}/g, "");
      if (plain) nodes.push(plain);
    }

    // match[1] = colon case (`{{char:ID}}`)、 match[2] = hyphen case (`{{char-ID}}`、 ID は
    // prefix 込み)、 match[3] = inner text。 colon / hyphen どちらの case でも charId は
    // charactersMap の key として lookup される (= colon は prefix を 1 度 strip、 hyphen は
    // 元から prefix 込みなので保持)。 両方 undefined なら keyword タグ。
    const charId = match[1] ?? match[2];
    const innerText = match[3] ?? "";

    if (charId !== undefined) {
      // {{char:ID}} / {{char-ID}} タグ: キャラクターカラーで色付け
      const char = charactersMap.get(charId);
      const color = colorOverride ?? (char !== undefined ? char.color : DEFAULT_COLOR);
      nodes.push(
        createElement("span", { style: { color }, key: tagIndex }, innerText),
      );
    } else {
      // {{keyword}} タグ: 固定のキーワードカラー
      nodes.push(
        createElement(
          "span",
          { style: { color: colorOverride ?? "#F59E0B" }, key: tagIndex },
          innerText,
        ),
      );
    }

    tagIndex += 1;
    lastIndex = matchEnd;
  }

  // タグ後に残ったプレーンテキストを追加（未知の{{...}}タグは除去）
  if (lastIndex < text.length) {
    const plain = text.slice(lastIndex).replace(/\{\{.*?\}\}/g, "");
    if (plain) nodes.push(plain);
  }

  return nodes;
}

/**
 * インラインマークアップを除去してプレーンテキストを返す。
 * スクリーンリーダーへの読み上げ通知（aria-live）など、装飾不要で
 * テキスト内容だけが必要な場面で使う。char/keyword タグは内側テキストを
 * 残し、未知の {{...}} タグは parseInlineMarkup と同様に除去する。
 */
export function stripInlineMarkup(text: string): string {
  return text
    .replace(MARKUP_REGEX, "$3")
    .replace(/\{\{.*?\}\}/g, "");
}
